
//#region ENUM AuthPersistenceEnum
/**
 * ### AuthPersistenceEnum
 * Enum representing the persistence behavior for Firebase Authentication.
 *
 * This setting determines how the authentication state is stored and restored
 * across sessions or tabs.
 *
 * @see https://firebase.google.com/docs/auth/web/auth-state-persistence
 * @enum {string}
 */
export enum AuthPersistenceEnum {
  /**
   * The authentication state persists even after the browser or tab is closed.
   * Stored in `localStorage`.
   *
   * Corresponds to `firebase.auth.Auth.Persistence.LOCAL`.
   *
   * @example
   * const persistence = AuthPersistenceEnum.local;
   */
  local = 'local',

  /**
   * Authentication state persists only in the current session/tab.
   * Stored in `sessionStorage`.
   *
   * Corresponds to `firebase.auth.Auth.Persistence.SESSION`.
   *
   * @example
   * const persistence = AuthPersistenceEnum.session;
   */
  session = 'session',

  /**
   * No persistence; user signed out on page refresh or tab close.
   *
   * Corresponds to `firebase.auth.Auth.Persistence.NONE`.
   *
   * @example
   * const persistence = AuthPersistenceEnum.none;
   */
  none = 'none',
}

//#endregion

//#region ENUM AuthPersistenceEnumHumanReadable
/**
 * ### AuthPersistenceEnumHumanReadable
 * Human-readable labels for each member of `AuthPersistenceEnum`.
 *
 * These strings are suitable for UI display.
 *
 * @enum {string}
 */
export enum AuthPersistenceEnumHumanReadable {
  /** Persistent login stored locally */
  local = 'Persistent (Local Storage)',

  /** Session-only login, cleared on tab close */
  session = 'Session Only',

  /** No persistence; signed out on refresh */
  none = 'No Persistence',
}

//#endregion

//#region TYPE AuthPersistenceType
/**
 * ### AuthPersistenceType
 * String literal type of all keys in `AuthPersistenceEnum`.
 *
 * @type {string}
 * @example
 * const persistence: AuthPersistenceType = AuthPersistenceEnum.local;
 */
export type AuthPersistenceType = `${AuthPersistenceEnum}`;
//#endregion
